/**
 * Data hooks for audits + specs. Centralized so invalidation keys stay
 * consistent across the app.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface SpecOut {
  id: string;
  name: string;
  url: string;
  github_repo: string | null;
  github_path: string | null;
  active: boolean;
  last_fetched_at: string | null;
  created_at: string;
}

export interface SpecsListResponse {
  items: SpecOut[];
  used: number;
  limit: number; // -1 = unlimited
}

export interface AuditOut {
  id: string;
  trigger: string;
  status: 'queued' | 'running' | 'complete' | 'failed';
  overall_score: number | null;
  score_category: string | null;
  error_message: string | null;
  has_pdf: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface AuditListResponse {
  items: AuditOut[];
  total: number;
}

export interface FindingOut {
  id: string;
  dimension_id: string;
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
}

export interface AuditDetailOut extends AuditOut {
  results_json: unknown[] | null;
  findings: FindingOut[];
}

// ---- Specs ----

export function useSpecs() {
  return useQuery({
    queryKey: ['specs'],
    queryFn: () => api<SpecsListResponse>('/api/specs'),
  });
}

export function useCreateSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; url: string; github_repo?: string; github_path?: string }) =>
      api<SpecOut>('/api/specs', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['specs'] });
      qc.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}

export function useDeleteSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/specs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['specs'] });
      qc.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}

// ---- Audits ----

export function useAudits(limit = 50) {
  return useQuery({
    queryKey: ['audits', { limit }],
    queryFn: () => api<AuditListResponse>(`/api/audits?limit=${limit}`),
  });
}

export function useLatestAudit() {
  return useQuery({
    queryKey: ['audits', 'latest'],
    queryFn: () => api<AuditListResponse>('/api/audits?limit=1'),
    select: (data) => data.items[0] ?? null,
    staleTime: 10_000,
  });
}

type RefetchInterval = number | false | ((query: any) => number | false | undefined);

export function useAudit(id: string | undefined, opts?: { refetchInterval?: RefetchInterval }) {
  return useQuery({
    queryKey: ['audit', id],
    queryFn: () => api<AuditDetailOut>(`/api/audits/${id}`),
    enabled: Boolean(id),
    refetchInterval: opts?.refetchInterval,
  });
}

export function useCreateAudit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { dimensions?: string[] } = {}) =>
      api<AuditOut>('/api/audits', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['audits'] });
    },
  });
}
